<!--
  One banner-style section per theme/collection. Each section is sized
  16:9 to fit a single viewport so it captures cleanly as a JetBrains-
  Marketplace media image (recommended 1280x720).

  Layout pieces: theme logo + category + appearance, theme name, colour
  palette dots, tag pills, description, FEATURED badge, screenshot
  frame, and the "made by Codigrate" footer.
-->
<!--
  The `sections` array contains a single pass over the products with a
  marketing-led badge:
    - individual themes  - product.editorialLabel uppercased
                           (e.g. "EDITOR'S CHOICE") under the static
                           "FEATURED" label. Falls back to "CURATED"
                           for themes that haven't been labelled yet.
    - the collection     - "N Themes" (running count of individual
                           themes) under "BUNDLE", so the All-In-One
                           screenshot communicates scope instead of
                           an editorial hook.
  Single *ngFor with `trackBy` keyed on `section.trackKey`
  (product.key) keeps each rendered section unique. `data-variant` on
  the DOM is preserved for any capture tooling that targets the section
  by attribute.
-->
<!--
  Each section forces its OWN dark/light vars so the per-product
  appearance always wins over the global app theme. Dark-appearance
  products apply `.dark-theme`; everything else (light, or unset)
  applies `.light-theme`. Otherwise a light-theme product would
  inherit dark CSS vars when the global app is in dark mode (and
  vice-versa) and the marketplace screenshot would render with the
  wrong palette.
-->
<section class="screenshot-section"
         *ngFor="let section of sections; trackBy: trackBySectionKey"
         [class.dark-theme]="section.isDarkTheme"
         [class.light-theme]="!section.isDarkTheme"
         [attr.data-key]="section.product.key"
         [attr.data-variant]="section.variant">

  <!-- Blurred theme background fills the section behind the glass card. -->
  <div class="screenshot-background"
       *ngIf="section.product.backgroundImages?.length"
       [style.background-image]="'url(' + section.product.backgroundImages[0].src + ')'">
  </div>

  <div class="screenshot-card"
       [style.--plugin-primary]="section.product.designTokens?.primaryColor"
       [style.--plugin-secondary]="'color-mix(in srgb, var(--plugin-primary) 80%, white)'"
       [style.--plugin-tertiary]="section.product.designTokens?.tertiaryColor">

    <!-- Corner SVG accents in the theme's own gradient. Per-section
         gradient IDs so multiple sections on one page don't collide.
         The `.dark` modifier swaps the gradient stops to the lighter
         variant used when the surrounding section is dark. -->
    <svg class="logo-left" [class.dark]="section.isDarkTheme"
         xmlns="http://www.w3.org/2000/svg"
         xmlns:xlink="http://www.w3.org/1999/xlink"
         viewBox="0 0 210 297"
         height="297mm"
         width="210mm">
      <defs>
        <linearGradient
          y2="165.93872"
          x2="224.92535"
          y1="169.14873"
          x1="124.6287"
          gradientTransform="matrix(-3.8992867,0,0,3.8992867,887.45881,-514.3054)"
          gradientUnits="userSpaceOnUse"
          [id]="'screenshotsLinearGradient_' + section.product.key"
          [attr.xlink:href]="'#screenshotsLogoColor_' + section.product.key"/>
        <linearGradient
          y2="165.93872"
          x2="224.92535"
          y1="169.14873"
          x1="124.6287"
          gradientTransform="matrix(-3.8992867,0,0,3.8992867,887.45881,-514.3054)"
          gradientUnits="userSpaceOnUse"
          [id]="'screenshotsLinearGradientReversed_' + section.product.key"
          [attr.xlink:href]="'#screenshotsLogoColorReversed_' + section.product.key"/>
        <linearGradient
          x1="0"
          y1="0"
          x2="1"
          y2="0"
          gradientUnits="userSpaceOnUse"
          gradientTransform="matrix(135.46671,0,0,-135.46671,1268.2878,631.59055)"
          spreadMethod="pad"
          [id]="'screenshotsLogoColor_' + section.product.key">
          <stop class="stop-theme-color"
                stop-color="var(--plugin-primary, var(--theme-color))"
                offset="0"/>
          <stop class="stop-theme-color-secondary"
                stop-color="var(--plugin-secondary, var(--theme-color-secondary))"
                offset="1"/>
        </linearGradient>
        <linearGradient
          x1="0"
          y1="0"
          x2="1"
          y2="0"
          gradientUnits="userSpaceOnUse"
          gradientTransform="matrix(135.46671,0,0,-135.46671,1268.2878,631.59055)"
          spreadMethod="pad"
          [id]="'screenshotsLogoColorReversed_' + section.product.key">
          <stop class="stop-theme-color-secondary"
                stop-color="var(--plugin-secondary, var(--theme-color-secondary))"
                offset="0"/>
          <stop class="stop-theme-color"
                stop-color="var(--plugin-primary, var(--theme-color))"
                offset="1"/>
        </linearGradient>
      </defs>
      <path
        [style.fill]="'url(#screenshotsLinearGradient_' + section.product.key + ')'"
        style="fill-opacity:1;stroke:none;stroke-width:4.17227"
        d="m 178.50387,213.25342 c 14.21454,-12.55147 17.57518,-39.48208 6.87786,-55.13809 v 0 c -8.67477,-16.8641 -33.95747,-26.73174 -51.76202,-20.2132 v 0 L 79.25799,83.536554 162.79062,8e-5 102.34432,0 18.32417,83.536554 103.15283,168.36917 c -6.52233,17.80465 3.34909,43.08736 20.21314,51.76217 v 0 c 6.24325,4.26283 14.27194,6.29676 22.47262,6.29676 v 0 c 12.368,0 25.11827,-4.62604 32.66528,-13.17468"/>
    </svg>

    <svg class="logo-right" [class.dark]="section.isDarkTheme"
         xmlns="http://www.w3.org/2000/svg"
         xmlns:xlink="http://www.w3.org/1999/xlink"
         viewBox="0 0 210 297"
         height="297mm"
         width="210mm">
      <path
        [style.fill]="'url(#screenshotsLinearGradientReversed_' + section.product.key + ')'"
        style="fill-opacity:1;stroke:none;stroke-width:3.8232"
        d="M 31.496131,83.498219 C 17.281586,96.049685 13.920954,122.9803 24.61827,138.63631 v 0 c 8.67477,16.8641 33.95747,26.73174 51.762024,20.2132 v 0 l 54.361716,54.36558 -83.532627,83.53647 60.446297,8e-5 84.02015,-83.53655 -84.82866,-84.83262 C 113.3695,110.57782 103.49808,85.295107 86.634028,76.620298 v 0 C 80.390777,72.357466 72.362085,70.323536 64.161412,70.323536 v 0 c -12.368001,0 -25.118271,4.626039 -32.665281,13.174683"/>
    </svg>

    <div class="screenshot-content">

      <div class="screenshot-text">

        <!-- Two-column title block: plugin icon on the left, category
             eyebrow ("NATURE — DARK") stacked above the theme name on
             the right. The icon vertically centers against the combined
             meta + name column so it spans both rows as a leading
             column. -->
        <div class="theme-title-block">
          <img *ngIf="section.product.logo"
               class="theme-icon"
               [src]="section.product.logo"
               [alt]="section.product.name + ' icon'"
               loading="lazy"
               decoding="async">

          <div class="theme-title-text">
            <div class="theme-meta">
              <p class="theme-category">{{ section.product.category || section.productLine.name }}</p>
              <span class="theme-meta-dash" aria-hidden="true">—</span>
              <p class="theme-appearance">
                {{ section.product.appearance || 'TEMPLATE WITH PHOTOS' }}
              </p>
            </div>
            <h1 class="theme-name">{{ section.product.name }}</h1>
          </div>
        </div>

        <!-- Identity block: color palette dots, then tag pills, sit
             directly below the icon + title row so the theme's palette
             and traits read as part of the identity header, ahead of
             the descriptive copy. -->
        <div class="theme-color-tags" *ngIf="section.product.compactPalette?.length">
          <div *ngFor="let color of section.product.compactPalette"
               class="color-tag-circle"
               [style.background-color]="color"></div>
        </div>

        <div class="theme-tags" *ngIf="section.product.tags?.length">
          <span *ngFor="let tag of section.product.tags" class="theme-tag">
            <span class="theme-tag-text">{{ tag }}</span>
          </span>
        </div>

        <p class="theme-description">{{ section.product.description }}</p>

        <!-- Badge: value above, label below. The actual text is
             precomputed per section in the component, so the template
             stays generic:
               individual themes  - product.editorialLabel uppercased
                                    (e.g. "EDITOR'S CHOICE") under
                                    FEATURED. Falls back to "CURATED"
                                    for themes that haven't been
                                    labelled yet.
               collection         - "N Themes" under BUNDLE. -->
        <div class="theme-badge">
          <span class="theme-badge-value">{{ section.displayBadge }}</span>
          <span class="theme-badge-label">{{ section.displayBadgeLabel }}</span>
        </div>

        <!-- Brand tagline footer. Second piece pulled from your
             references - the marketplace screenshot should always
             attribute the publisher. -->
        <div class="made-by">
          <span class="made-by-text">made by</span>
          <img class="made-by-logo" [src]="section.brandLogo" [alt]="BRAND.name">
        </div>
      </div>

      <!-- Screenshot frame - no platform pill, no Try/Get CTA, just
           the theme rendering framed cleanly. -->
      <div class="screenshot-frame" *ngIf="section.screenshot">
        <img [src]="section.screenshot.src"
             [alt]="section.product.name + ' screenshot'"
             class="screenshot-image"
             decoding="async">
      </div>
    </div>
  </div>
</section>
